import { useLayoutEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { brand } from '../theme/tokens';
import { brandAssets } from '../theme/brand-assets';
import { useLanguage } from './language-context';
import { SignOutLabel, useSigningOut } from './sign-out-progress';
import { Icon } from './icon';
import { railGroups, navItemForPath } from './nav-items';
import type { ShellBadges } from './nav-items';
import { surfaceOrigin } from './surface-links';
import { formatNumber } from '@bookingdei/i18n/format-number';
import { formatMessage } from '@bookingdei/i18n/interpolate';
import { appVersion, appVersionDetail } from './app-build';
import './rail.css';

// The desktop glass rail (CB-17, customer design Others.dc.html L38-106): brand
// lockup, role chip, three labelled groups — Bookings {Home, My bookings} · Mine
// {Saved, Reviews} · Account {Account, Help} — with the action badges, the help/contact
// promo card (hidden on short windows, where it would push Help out of view) and Sign
// out. The active pill slides from the previous item (transform only, 200 ms, skipped
// under reduced motion). The designer credit plate on the lockup is not shipped (INF-1);
// the version chip beside it is the build's (app-build.ts), never a literal.
// Hidden below the 760 px breakpoint, where the tab bar takes over (rail.css).

// Offset of the last active pill, kept across renders/remounts so the slide starts
// from wherever the pill was (design `LAST_PILL_TOP`).
let lastPillTop: number | null = null;

function prefersReducedMotion(): boolean {
  return typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function Rail({ badges, onSignOut }: { badges: ShellBadges; onSignOut: () => void }) {
  const { t, lang } = useLanguage();
  const signingOut = useSigningOut();
  const { pathname } = useLocation();
  const activeId = navItemForPath(pathname)?.id;
  const activeRef = useRef<HTMLAnchorElement | null>(null);

  useLayoutEffect(() => {
    const el = activeRef.current;
    if (!el) return;
    const top = el.offsetTop;
    const prev = lastPillTop;
    lastPillTop = top;
    if (prev === null || prev === top || typeof el.animate !== 'function' || prefersReducedMotion()) return;
    el.style.willChange = 'transform';
    const anim = el.animate([{ transform: `translateY(${prev - top}px)` }, { transform: 'none' }], {
      duration: 200,
      easing: 'cubic-bezier(.16,1,.3,1)',
    });
    const done = () => {
      el.style.willChange = '';
    };
    anim.onfinish = done;
    anim.oncancel = done;
  }, [activeId]);

  const contactHref = `${surfaceOrigin('website') ?? ''}/contact`;

  return (
    <aside className="dash-rail" aria-label={t('user.nav.primary')}>
      <Link to="/" className="dash-rail__lockup" aria-label={brand.name}>
        <img className="dash-rail__logo" src={brandAssets.lockup} alt="" />
        <img className="dash-rail__logo dash-rail__logo--rev" src={brandAssets.lockupReversed} alt="" />
        {appVersion && (
          <span className="dash-rail__ver" translate="no" title={formatMessage(lang, 'user.shell.version', { version: appVersionDetail ?? appVersion })}>
            v{appVersion}
          </span>
        )}
      </Link>
      <div className="dash-rail__role">
        <span className="dash-rail__chip">
          <Icon name="shield" size={10} stroke={2} />
          <span>{t('user.shell.role')}</span>
        </span>
      </div>

      <nav className="dash-rail__nav">
        {railGroups.map((group) => (
          <div key={group.id} className="dash-rail__group">
            <div className="dash-rail__group-label">{t(group.key)}</div>
            {group.items.map((item) => {
              const active = item.id === activeId;
              const count = item.badge ? badges[item.badge] : 0;
              return (
                <NavLink
                  key={item.id}
                  to={item.to}
                  end={item.to === '/'}
                  ref={active ? activeRef : undefined}
                  className={active ? 'dash-rail__item is-active' : 'dash-rail__item'}
                  aria-current={active ? 'page' : undefined}
                >
                  <span className="dash-rail__icon">
                    <Icon name={item.icon} />
                  </span>
                  <span className="dash-rail__label">{t(item.key)}</span>
                  {count > 0 && <span className="dash-rail__badge">{formatNumber(lang, count)}</span>}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="dash-rail__help">
        <div className="dash-rail__help-text">
          <span className="dash-rail__help-title">{t('user.shell.help_card.title')}</span>
          <span className="dash-rail__help-sub">{t('user.shell.help_card.sub')}</span>
        </div>
        <div className="dash-rail__help-actions">
          <Link to="/help" className="dash-rail__help-btn dash-rail__help-btn--primary">
            {t('user.shell.help_card.help')}
          </Link>
          <a href={contactHref} className="dash-rail__help-btn">
            {t('user.shell.help_card.contact')}
          </a>
        </div>
      </div>

      <button type="button" className="dash-rail__signout" onClick={onSignOut} disabled={signingOut} aria-busy={signingOut}>
        <SignOutLabel label={t('user.shell.sign_out')} />
      </button>
    </aside>
  );
}
